import React, { useState, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  MenuItem,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { ArrowForward, LocationOn, LocalShipping, SwapHoriz, Restore as RestoreIcon } from '@mui/icons-material';
import { StandardDialog, TabConfig } from '../base';
import { DeleteButton } from '../../buttons';
import AutoSearchBadge from '../../badges/AutoSearchBadge';
import AutoNegoBadge from '../../badges/AutoNegoBadge';
import AutoSearchDialog from '../auto/AutoSearchDialog';
import AutoNegoDialog from '../auto/AutoNegoDialog';
import { InfoTooltip } from '../../tooltips/infoTooltip';
import { EmptyState } from '../../emptyState/emptyState';
import {
  Address,
  LoadSecurity,
  SwappableBodyType,
  TrailerEquipment,
  TrailerProperty,
  TrailerType,
  VehicleType,
  TrackerProvider,
  TrackerProviders,
  VehicleWithDriver,
} from '../../../models/vehicle';
import { BookingsTable } from '../../tables';
import { apiPost, apiDelete, apiGet } from '../../../services/apiClient';
import { getAutoSessions, getAutoSearchHistory } from '../../../services/autoSessionService';
import { getVehicle } from '../../../services/vehicleService';
import { Booking } from '../../../models/booking';
import { useVehicleContext } from '../../../context/vehicleContext';
import { useTenant } from '../../../hooks';
import { VehicleAutoSearch, AutoSearchHistoryEntry } from '../../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession } from '../../../models/vehicleAutoNegoSession';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import { LocationParameter, PostalCodeArea } from '../../../models/search';
import DateTimeInput from '../../inputs/DateTimeInput';
import { SelectInput, TextInput, CountryAutocomplete } from '../../inputs';
import { formatDateTimeLocal } from '../../../utils/dateTime';
import { formatDateTime, enumToOptions } from '../../../utils/misc';
import { UserPlatformSubRole } from '../../../models/user';
import { parseCoordinates, formatCoordinates } from '../../../utils/coordinateUtils';
import * as yup from 'yup';
import { API_ENDPOINTS } from '../../../config/apiEndpoints';
import { Employee } from '../../userMenu/teamManagement';
import { AuthContext } from '../../../context/authContext';

// Helper function to format enum values (SNAKE_CASE -> Title Case)
const formatEnumValue = (value: string): string => value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

// Helper to render multi-select values with chips
const renderMultiSelectValue = (selected: string[]) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {selected.map((value) => (
      <Chip
        key={value}
        label={formatEnumValue(value)}
        size="small"
        color="primary"
        variant="outlined"
      />
    ))}
  </Box>
);

// Reusable component for multi-select enum fields
const EnumMultiSelect = <T extends string>({
  label,
  value,
  onChange,
  valuesList,
}: {
  label: string;
  value: T[];
  onChange: (v: T[]) => void;
  valuesList: T[];
}) => (
    <SelectInput
      label={label}
      multiple
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        onChange((Array.isArray(v) ? v : [v]) as T[]);
      }}
      renderValue={(selected) => renderMultiSelectValue(selected as string[])}
      size="small"
      sx={styles.formControl}
    >
      {valuesList.map(v => (
        <MenuItem key={v} value={v}>{formatEnumValue(v)}</MenuItem>
      ))}
    </SelectInput>
  );

// Styling constants
const styles = {
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, 1fr)',
      lg: 'repeat(4, 1fr)',
      xl: 'repeat(4, 1fr)'
    },
    gap: {
      xs: 2,
      sm: 2.5,
      md: 3
    }
  },
  sectionHeading: {
    mb: {
      xs: 2,
      sm: 2.5,
      md: 3
    },
    color: 'text.dark',
    fontWeight: 600,
    fontSize: {
      xs: '1.1rem',
      sm: '1.25rem',
      md: '1.5rem'
    }
  },
  sectionContainer: {
    p: {
      xs: 1.5,
      sm: 2,
      md: 2.5,
      lg: 3
    }
  },
  formControl: {
    '&.MuiFormControl-sizeSmall .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, -50%)',
    },
  }
};

type Props = {
  open: boolean;
  onClose: () => void;
  truck: VehicleWithDriver | null;
  onSaved?: () => void;
  onSuccess?: (message: string) => void;
};


type FormValues = Omit<Partial<VehicleWithDriver>,
  'id' | 'createdAt' | 'tenant_id' | 'lastTimeSearchedAt' | 'tenantOverrides' |
  'currentPositionSyncedAt' | 'currentPositionNextSyncAt' | 'currentPositionAutoSyncIntervalMin' |
  'maxDailyAutoSearches' | 'maxDailyAutoNegos' | 'currentPosition' | 'emptyIn'
> & {
  currentPosition: Address; // Required - always initialized
  emptyIn: Address; // Required - always initialized
};

type FieldErrors = Record<string, string | undefined>;

// Default empty address structure
const createEmptyAddress = (): Address => ({
  objectType: 'Address',
  city: '',
  country: '' as CountryCode,
  geoCoordinate: {
    longitude: 0,
    latitude: 0
  },
  geocoded: false,
  postalCode: '',
  streetOrPostbox: ''
});

// Strip empty / zero placeholder values before sending to the API.
const cleanAddressForApi = (addr: any): any => {
  if (!addr || typeof addr !== 'object') return undefined;
  const cleaned: any = {};
  for (const k of Object.keys(addr)) {
    const v = addr[k];
    if (v === '' || v === undefined || v === null) continue;
    cleaned[k] = v;
  }
  if (cleaned.geoCoordinate
      && cleaned.geoCoordinate.latitude === 0
      && cleaned.geoCoordinate.longitude === 0) {
    delete cleaned.geoCoordinate;
  }
  const meaningful = Object.keys(cleaned).filter(k => k !== 'objectType' && k !== 'geocoded');
  if (meaningful.length === 0) return undefined;
  return cleaned;
};

// Default initial form values
const getInitialFormValues = (): FormValues => ({
  truckPlate: '',
  driverFirstName: '',
  driverLastName: '',
  trailerPlate: '',
  name: '',
  manufacturer: '',
  model: '',
  modelYear: undefined,
  colour: '',
  chassisNumber: '',
  type: undefined,
  swappable: false,
  swapBodyType: undefined,
  trailerProperty: [],
  trailerType: undefined,
  equipment: [],
  loadSecurity: [],
  currentPosition: createEmptyAddress(),
  emptyIn: createEmptyAddress(),
  emptyAt: undefined,
  maximumWeightT: undefined,
  maximumLengthM: undefined,
  regCountry: '',
  maximumCabotages: undefined,
  tenantPreferredRoute: '',
  tracker: undefined
});

// Validation schema for Truck Information section - all fields required
const validationSchema = yup.object({
  truckPlate: yup.string().required('Truck plate is required').trim(),
  trailerPlate: yup.string().optional(),
  manufacturer: yup.string().required('Manufacturer is required').trim(),
  model: yup.string().required('Model is required').trim(),
  modelYear: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('Model year is required')
    .min(1900, 'Invalid year')
    .max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
  colour: yup.string().required('Colour is required').trim(),
  chassisNumber: yup.string().required('Chassis number is required').trim(),
  maximumLengthM: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('Maximum length is required')
    .min(0, 'Length must be positive'),
  maximumWeightT: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('Maximum weight is required')
    .min(0, 'Weight must be positive'),
  tenantPreferredRoute: yup.string().required('Assigned route is required').trim(),
});

/**
 * Builds the payload for API requests from form values
 */
const buildPayload = (vals: FormValues, changed?: any) => {
  const src = changed && Object.keys(changed).length > 0 ? changed : vals;
  const payload: any = { ...src };

  // Date to timestamp conversion
  if ('emptyAt' in payload) {
    payload.emptyAt = payload.emptyAt instanceof Date
      ? payload.emptyAt.getTime()
      : payload.emptyAt
        ? new Date(payload.emptyAt).getTime()
        : undefined;
  }

  // Server replaces the whole address object, so always send the full vals (not just the diff).
  if ('currentPosition' in payload) {
    payload.currentPosition = cleanAddressForApi(vals.currentPosition);
  }
  if ('emptyIn' in payload) {
    payload.emptyIn = cleanAddressForApi(vals.emptyIn);
  }

  // Number casts
  ['modelYear', 'maximumCabotages', 'maximumWeightT', 'maximumLengthM'].forEach(k => {
    if (payload[k] !== undefined && payload[k] !== '') {
      payload[k] = Number(payload[k]);
    }
  });

  // Null/empty rules
  if ('trailerPlate' in payload) {
    payload.trailerPlate = payload.trailerPlate ? payload.trailerPlate : null;
  }
  if ('swapBodyType' in payload) {
    payload.swapBodyType = payload.swapBodyType || undefined;
  }
  if ('regCountry' in payload) {
    payload.regCountry = payload.regCountry || '';
  }
  if ('tenantPreferredRoute' in payload) {
    payload.tenantPreferredRoute = payload.tenantPreferredRoute || '';
  }
  if ('managerUserId' in payload) {
    payload.manager_user_id = payload.managerUserId;
  }
  return payload;
};

export default function VehicleSettingsDialog({ open, onClose, truck, onSaved, onSuccess }: Props) {
  const { t } = useTranslation();

  // Truck fields configuration
  const truckFields = [
    { name: 'truckPlate', label: t('Truck Plate'), required: true },
    { name: 'trailerPlate', label: t('Trailer Plate'), required: false },
    { name: 'manufacturer', label: t('Manufacturer'), required: true },
    { name: 'model', label: t('Model'), required: true },
    { name: 'modelYear', label: t('Model Year'), required: true, type: 'number' },
    { name: 'colour', label: t('Colour'), required: true },
    { name: 'chassisNumber', label: t('Chassis Number'), required: true },
    { name: 'maximumLengthM', label: t('Maximum length in meters'), required: true, type: 'number' },
    { name: 'maximumWeightT', label: t('Maximum weight in tons'), required: true, type: 'number' },
  ] as const;

  const authContext = useContext(AuthContext);
  const { refreshVehicles } = useVehicleContext();
  const { tenant } = useTenant();
  const [tenantRoutes, setTenantRoutes] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<AutoSearchHistoryEntry['searchHistory']>([]);
  const [autoSearchSession, setAutoSearchSession] = useState<VehicleAutoSearch | null>(null);
  const [autoNegoSession, setAutoNegoSession] = useState<VehicleAutoNegoSession | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [values, setValues] = useState<FormValues>(getInitialFormValues());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [autoSearchModalOpen, setAutoSearchModalOpen] = useState(false);
  const [autoNegoModalOpen, setAutoNegoModalOpen] = useState(false);

  const [statsFrom, setStatsFrom] = useState<Date | undefined>(undefined);
  const [statsTo, setStatsTo] = useState<Date | undefined>(undefined);
  const [statsBookings, setStatsBookings] = useState<Booking[] | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [managerOptions, setManagerOptions] = useState<Employee[]>([]);
  const [managerUserId, setManagerUserId] = useState<number | undefined>(undefined);
  // Fetch eligible managers (TRUCK_MANAGER or DIRECTOR) when modal opens
  useEffect(() => {
    const fetchManagers = async () => {
      if (!tenant?.id) return;
      try {
        const url = API_ENDPOINTS.tenant.employees(tenant.id);
        const res = await apiGet(url);
        const data = await res.json();
        const eligible = (data.result || []).filter((u: Employee) => u.subRole === UserPlatformSubRole.TRUCK_MANAGER || u.subRole === UserPlatformSubRole.DIRECTOR);
        setManagerOptions(eligible);
      } catch {
        setManagerOptions([]);
      }
    };
    if (open) fetchManagers();
  }, [open, tenant]);
  // Set initial manager when truck changes
  useEffect(() => {
    if (open && truck) {
      setManagerUserId(truck.manager_user_id);
    }
  }, [open, truck]);

  // Sync managerUserId into values for change detection and payload
  useEffect(() => {
    setValues((prev) => ({ ...prev, managerUserId: managerUserId }));
  }, [managerUserId]);

  // Extract tenant routes when tenant data becomes available
  useEffect(() => {
    if (tenant?.preferredRoutes) {
      const routeKeys = Object.keys(tenant.preferredRoutes);
      setTenantRoutes(routeKeys);
    }
  }, [tenant]);

  useEffect(() => {
    const fetchSearchHistory = async () => {
      if (!open || !truck?.id) return;
      try {
        const [{ autoSearches, autoNegoSessions }, historyResult] = await Promise.all([
          getAutoSessions([truck.id]),
          getAutoSearchHistory([truck.id]),
        ]);

        // Store both sessions for passing to BookingsTable
        setAutoSearchSession(autoSearches[truck.id] || null);
        setAutoNegoSession(autoNegoSessions[truck.id] || null);

        // Set search history from history endpoint (merged across all sessions)
        const vehicleHistory = historyResult[truck.id];
        setSearchHistory(vehicleHistory?.searchHistory ?? []);
      } catch {
        setSearchHistory([]);
        setAutoSearchSession(null);
        setAutoNegoSession(null);
      }
    };
    fetchSearchHistory();
  }, [open, truck]);

  // Handlers for badge clicks
  const handleAutoSearchClick = () => {
    setAutoSearchModalOpen(true);
  };

  const handleAutoNegoClick = () => {
    setAutoNegoModalOpen(true);
  };

  const handleAutoSessionSuccess = async () => {
    if (!truck?.id) return;
    try {
      const { autoSearches, autoNegoSessions } = await getAutoSessions([truck.id]);
      setAutoSearchSession(autoSearches[truck.id] || null);
      setAutoNegoSession(autoNegoSessions[truck.id] || null);
      refreshVehicles();
    } catch {
      toast.error(t('Failed to refresh auto sessions'));
    }
  };

  // Normalize values for comparison - handles special cases like swappable/swapBodyType
  const normalizeValues = (vals: FormValues): FormValues => {
    const normalized = { ...vals };
    // If swappable is false, ensure swapBodyType is undefined for comparison
    if (normalized.swappable === false) {
      normalized.swapBodyType = undefined;
    }
    return normalized;
  };

  // Clear errors when modal closes
  useEffect(() => {
    if (!open) {
      setFieldErrors({});
    }
  }, [open]);

  // Initialize form values when modal opens or truck data changes
  useEffect(() => {
    if (open && truck) {
      // Clear validation errors when modal opens
      setFieldErrors({});
      
      const newValues: FormValues = {
        driverFirstName: truck.driverFirstName || '',
        driverLastName: truck.driverLastName || '',
        truckPlate: truck.truckPlate || '',
        trailerPlate: truck.trailerPlate || '',
        name: truck.name || '',
        manufacturer: truck.manufacturer || '',
        model: truck.model || '',
        modelYear: truck.modelYear,
        colour: truck.colour || '',
        chassisNumber: truck.chassisNumber || '',
        type: truck.type,
        swappable: truck.swappable || false,
        swapBodyType: truck.swapBodyType,
        trailerProperty: truck.trailerProperty || [],
        trailerType: truck.trailerType,
        equipment: truck.equipment || [],
        loadSecurity: truck.loadSecurity || [],
        currentPosition: truck.currentPosition || createEmptyAddress(),
        emptyIn: truck.emptyIn || createEmptyAddress(),
        emptyAt: truck.emptyAt ? new Date(truck.emptyAt) : undefined,
        maximumLengthM: truck.maximumLengthM ? Number(truck.maximumLengthM) : undefined,
        maximumWeightT: truck.maximumWeightT ? Number(truck.maximumWeightT) : undefined,
        regCountry: truck.regCountry || '',
        maximumCabotages: truck.maximumCabotages ? Number(truck.maximumCabotages) : undefined,
        tenantPreferredRoute: truck.tenantPreferredRoute || '',
        tracker: truck.tracker || undefined
      };
      setValues(newValues);
      // Store initial values as deep copy for comparison
      const deepCopy = (obj: FormValues): FormValues => {
        const copy = JSON.parse(JSON.stringify(obj));
        // Restore Date objects - use original date to avoid precision issues
        if (obj.emptyAt) {
          copy.emptyAt = new Date(obj.emptyAt.getTime());
        }
        return copy;
      };
      // Also store managerUserId for change detection
      deepCopy.managerUserId = truck.manager_user_id;
      setInitialValues(deepCopy(newValues));
    }
  }, [open, truck]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | { target: { name?: string; value: unknown } }) => {
    const name = e.target.name as keyof FormValues;
    let value: any = e.target.value;

    if (name === 'swappable') {
      value = (e.target as HTMLInputElement).checked;
    }

    if (name === 'modelYear') {
      value = value === '' ? undefined : Number(value);
    }
    if (name === 'maximumWeightT' && value !== '') value = Number(value);
    if (name === 'maximumLengthM' && value !== '') value = Number(value);
    if (name === 'maximumCabotages' && value !== '') value = Number(value);

    setValues((prev) => ({ ...prev, [name]: value }));
  };

  // Handle nested address field changes
  const handleAddressChange = (addressType: 'currentPosition' | 'emptyIn', field: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));
  };

  // Handle coordinate changes
  const handleCoordinateChange = (addressType: 'currentPosition' | 'emptyIn', coordinateString: string) => {
    const coords = parseCoordinates(coordinateString);
    setValues((prev) => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        geoCoordinate: {
          latitude: coords?.latitude, //FIXME: Make sure that we have coordinates 
          longitude: coords?.longitude //FIXME: Make sure that we have coordinates 

        }
      }
    }));
  };

  const handleSave = async (changedFields?: any) => {
    if (!truck) return;

    // Validate required fields using yup
    try {
      await validationSchema.validate({
        truckPlate: values.truckPlate,
        trailerPlate: values.trailerPlate,
        manufacturer: values.manufacturer,
        model: values.model,
        modelYear: values.modelYear,
        colour: values.colour,
        chassisNumber: values.chassisNumber,
        maximumLengthM: values.maximumLengthM,
        maximumWeightT: values.maximumWeightT,
        tenantPreferredRoute: values.tenantPreferredRoute,
      }, { abortEarly: false });
      setFieldErrors({});
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const errors: FieldErrors = {};
        err.inner.forEach((error) => {
          if (error.path) {
            errors[error.path] = t(error.message);
          }
        });
        setFieldErrors(errors);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const payload = buildPayload(values, changedFields);

      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${truck.id}/update`, payload);

      refreshVehicles();

      if (onSaved) {
        onSaved();
      }

      toast.success(t('Truck settings updated successfully!'));

      // Keep button disabled (loading=true) for the whole post-success pause until the dialog closes.
      setTimeout(() => {
        setLoading(false);
        onClose();
      }, 1500);
    } catch {
      toast.error(t('Failed to update truck settings'));
      setLoading(false);
    }
  };

  const handleDeleteTruck = async () => {
    if (!truck) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await apiDelete(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${truck.id}/delete`);

      if (!response.ok) {
        toast.error(t('Failed to delete truck'));
        return;
      }

      // Refresh vehicles in context after successful deletion
      refreshVehicles();

      // Close both dialogs
      setDeleteConfirmOpen(false);
      onClose();

      // Show success notification via callback
      if (onSuccess) {
        toast.success(t('Truck deleted successfully!'));
      }

      // Call onSaved callback if provided
      if (onSaved) {
        onSaved();
      }

    } catch {
      toast.error(t('Failed to delete truck'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestoreTruck = async () => {
    if (!truck) return;
    setLoading(true);
    try {
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${truck.id}/update`, { isActive: true });
      refreshVehicles();
      toast.success(t('Truck restored successfully!'));
      onClose();
    } catch {
      toast.error(t('Failed to restore truck'));
    } finally {
      setLoading(false);
    }
  };

  if (!open || !truck) return null;

  const renderTruckDetails = () => (
    <Box sx={styles.sectionContainer}>
      <Typography variant="h6" sx={styles.sectionHeading}>
        {t('Truck Information')}
      </Typography>
      <Box sx={styles.gridContainer}>
        {truckFields.map((f) => (
          <TextInput
            key={f.name}
            name={f.name}
            label={f.label}
            type={(f as any).type}
            value={(values as any)[f.name] ?? ''}
            onChange={(e) => {
              handleChange(e);
              if ((fieldErrors as any)[f.name]) {
                setFieldErrors(prev => ({ ...prev, [f.name]: undefined }));
              }
            }}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            fullWidth
            variant="outlined"
            size="small"
            required={(f as any).required}
            error={!!(fieldErrors as any)[f.name]}
            helperText={(fieldErrors as any)[f.name]}
          />
        ))}
        {/* Manager assignment dropdown */}
        <FormControl fullWidth size="small" sx={styles.formControl}>
          <SelectInput
            label={t('Assigned Manager')}
            value={managerUserId ?? ''}
            onChange={e => setManagerUserId(Number(e.target.value) || undefined)}
            options={managerOptions.map(u => ({ key: u.name, value: String(u.id) }))}
            size="small"
            helperText={t('Only users with TRUCK_MANAGER or DIRECTOR role are shown.')}
            disabled={authContext?.user?.subRole !== UserPlatformSubRole.DIRECTOR} // Only directors can change manager
          />
        </FormControl>
        <TextInput
          warning={!values.maximumCabotages}
          name="maximumCabotages"
          label={t('Maximum cabotages per country')}
          type="number"
          value={values.maximumCabotages}
          onChange={handleChange}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          fullWidth
          variant="outlined"
          size="small"
        />
        <CountryAutocomplete
          label={t('Registration Country')}
          value={values.regCountry}
          onChange={(code) => setValues((prev) => ({ ...prev, regCountry: code }))}
        />
        <SelectInput
          label={t('Vehicle Type')}
          value={values.type}
          onChange={(e) => setValues((prev) => ({ ...prev, type: e.target.value as VehicleType }))}
          options={enumToOptions(VehicleType)}
          size="small"
        />
        <SelectInput
          label={t('Tracker Provider')}
          value={values.tracker}
          onChange={(e) => setValues((prev) => ({ ...prev, tracker: e.target.value as TrackerProvider }))}
          options={TrackerProviders.map(provider => ({ key: provider, value: provider }))}
          size="small"
        />
      </Box>

      <Divider sx={{ my: { xs: 2, sm: 3, md: 4 } }} />

      <Typography variant="h6" sx={styles.sectionHeading}>
        {t('Trailer Configuration')}
      </Typography>
      <Box sx={styles.gridContainer}>
        <FormControl fullWidth variant="outlined" size="small" sx={styles.formControl}>
          <SelectInput
            value={values.trailerType}
            onChange={(e) => setValues((prev) => ({ ...prev, trailerType: e.target.value as TrailerType }))}
            label={t('Trailer Type')}
          >
            {Object.values(TrailerType).map((trailerType) => (
              <MenuItem key={trailerType} value={trailerType}>
                {formatEnumValue(trailerType)}
              </MenuItem>
            ))}
          </SelectInput>
        </FormControl>
        {values.swappable === true ? (<FormControl fullWidth variant="outlined" size="small" sx={styles.formControl}>
          <SelectInput
            value={values.swapBodyType ?? ''}
            onChange={(e) => {
              const value = e.target.value as SwappableBodyType | '';
              setValues((prev) => ({ ...prev, swapBodyType: value === '' ? undefined : value }));
            }}
            label={t('Swap Body Type')}
          >
            {Object.values(SwappableBodyType).map((swapBodyType) => (
              <MenuItem key={swapBodyType} value={swapBodyType}>
                {formatEnumValue(swapBodyType)}
              </MenuItem>
            ))}
          </SelectInput>
        </FormControl>) : ''}
        <EnumMultiSelect
          label={t('Trailer Properties')}
          value={values.trailerProperty || []}
          onChange={(selectedValues) => setValues((prev) => ({ ...prev, trailerProperty: selectedValues }))}
          valuesList={Object.values(TrailerProperty)}
        />
        <EnumMultiSelect
          label={t('Equipment')}
          value={values.equipment || []}
          onChange={(selectedValues) => setValues((prev) => ({ ...prev, equipment: selectedValues }))}
          valuesList={Object.values(TrailerEquipment)}
        />
        <EnumMultiSelect
          label={t('Load Security')}
          value={values.loadSecurity || []}
          onChange={(selectedValues) => setValues((prev) => ({ ...prev, loadSecurity: selectedValues }))}
          valuesList={Object.values(LoadSecurity)}
        />
      </Box>

      <Divider sx={{ my: { xs: 2, sm: 3, md: 4 } }} />

      <Typography variant="h6" sx={styles.sectionHeading}>
        {t('Route Preference')}
      </Typography>
      <Box sx={styles.gridContainer}>
        <SelectInput
          label={t('Assigned Route')}
          value={values.tenantPreferredRoute}
          onChange={(e) => {
            setValues((prev) => ({ ...prev, tenantPreferredRoute: e.target.value }));
            if (fieldErrors.tenantPreferredRoute) {
              setFieldErrors(prev => ({ ...prev, tenantPreferredRoute: undefined }));
            }
          }}
          options={tenantRoutes.map((routeKey) => ({ key: routeKey, value: routeKey.replaceAll('_', ' ') }))}
          size="small"
          required
          error={!!fieldErrors.tenantPreferredRoute}
          helperText={fieldErrors.tenantPreferredRoute}
        />
      </Box>
    </Box>
  );

  const renderDriverDetails = () => (
    <Box sx={styles.sectionContainer}>
      <Typography variant="h6" sx={styles.sectionHeading}>
        {t('Driver Information')}
      </Typography>
      <Box sx={styles.gridContainer}>
        <TextInput
          name="name"
          label={t('Driver Name')}
          value={values.driverFirstName + ' ' + values.driverLastName}
          onChange={handleChange}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          fullWidth
          variant="outlined"
          size="small"
        />
      </Box>
    </Box>
  );

  const renderLocationDetails = () => {
    const currentCoords = formatCoordinates(
      values?.currentPosition?.geoCoordinate?.latitude,
      values?.currentPosition?.geoCoordinate?.longitude
    );
    const emptyCoords = formatCoordinates(
      values?.emptyIn?.geoCoordinate?.latitude,
      values?.emptyIn?.geoCoordinate?.longitude
    );

    return (
      <Box sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {/* Unified Route Container */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr auto 1fr'
          },
          gap: { xs: 2, md: 2.5 },
          alignItems: 'start',
          position: 'relative'
        }}>
          {/* Current Position */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LocationOn sx={{ color: '#4caf50', fontSize: '1.25rem' }} />
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: (theme: any) => theme.typography.fontSizes.heading
              }}>
                {t('Current Position')}
              </Typography>
            </Box>
            
            {/* Primary fields */}
            <TextInput
              label={t('City')}
              value={values.currentPosition.city || ''}
              onChange={(e) => handleAddressChange('currentPosition', 'city', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
            />
            <CountryAutocomplete
              label={t('Country')}
              value={values.currentPosition.country}
              onChange={(code) => handleAddressChange('currentPosition', 'country', code)}
            />
            
            {/* Secondary field - Postal Code */}
            <TextInput
              label={t('Postal Code')}
              value={values.currentPosition.postalCode || ''}
              onChange={(e) => handleAddressChange('currentPosition', 'postalCode', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            
            {/* Coordinates */}
            <TextInput
              label={t('Coordinates (lat, lng)')}
              placeholder={t('e.g., 55.58886621397866, 23.765426156862443')}
              value={currentCoords}
              onChange={(e) => handleCoordinateChange('currentPosition', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
              helperText={t('Enter coordinates as: latitude, longitude')}
            />
          </Box>

          {/* Flow Arrow (desktop only) */}
          <Box sx={{
            position: 'relative',
            width: 80,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'flex-start',
            mt: 16, // Positioned lower to better align between sections
          }}>
            <ArrowForward
              sx={{
                fontSize: '3rem', // Bigger arrow
                color: 'primary.main',
                filter: 'drop-shadow(0 0 8px rgba(123, 97, 255, 0.6))',
                animation: 'arrowPulse 2s ease-in-out infinite',
                '@keyframes arrowPulse': {
                  '0%': {
                    opacity: 0.6,
                    transform: 'translateX(0)',
                    filter: 'drop-shadow(0 0 4px rgba(123, 97, 255, 0.4))',
                  },
                  '50%': {
                    opacity: 1,
                    transform: 'translateX(4px)',
                    filter: 'drop-shadow(0 0 12px rgba(123, 97, 255, 0.8))',
                  },
                  '100%': {
                    opacity: 0.6,
                    transform: 'translateX(0)',
                    filter: 'drop-shadow(0 0 4px rgba(123, 97, 255, 0.4))',
                  },
                },
              }}
            />
          </Box>

          {/* Empty In Position */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LocalShipping sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: (theme: any) => theme.typography.fontSizes.heading
              }}>
                {t('Empty In Position')}
              </Typography>
            </Box>
            
            {/* Primary fields */}
            <TextInput
              label={t('City')}
              value={values.emptyIn.city || ''}
              onChange={(e) => handleAddressChange('emptyIn', 'city', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
            />
            <CountryAutocomplete
              label={t('Country')}
              value={values.emptyIn.country}
              onChange={(code) => handleAddressChange('emptyIn', 'country', code)}
            />
            
            {/* Empty At - directly under city/country */}
            <DateTimeInput
              label={t('Empty At')}
              type="datetime"
              fullWidth
              size="small"
              value={formatDateTimeLocal(values.emptyAt) || ''}
              onChange={(value) => {
                let date: Date | undefined;
                if (!value) {
                  date = undefined;
                } else if (typeof value === 'object' && 'toDate' in value) {
                  date = (value as { toDate: () => Date }).toDate();
                } else if (Object.prototype.toString.call(value) === '[object Date]') {
                  date = value as Date;
                } else if (typeof value === 'string') {
                  date = new Date(value);
                } else {
                  date = undefined;
                }
                setValues((prev) => ({ ...prev, emptyAt: date }));
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            {/* Secondary field - Postal Code */}
            <TextInput
              label={t('Postal Code')}
              value={values.emptyIn.postalCode || ''}
              onChange={(e) => handleAddressChange('emptyIn', 'postalCode', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            
            {/* Coordinates */}
            <TextInput
              label={t('Coordinates (lat, lng)')}
              placeholder={t('e.g., 55.58886621397866, 23.765426156862443')}
              value={emptyCoords}
              onChange={(e) => handleCoordinateChange('emptyIn', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              fullWidth
              variant="outlined"
              size="small"
              helperText={t('Enter coordinates as: latitude, longitude')}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSettings = () => (
    <Box sx={styles.sectionContainer}>
      <Typography variant="h6" sx={styles.sectionHeading}>
        {t('Truck Settings')}
      </Typography>
      {truck.isActive === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('This truck is deleted. Restore it below to edit its settings.')}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5, md: 3 } }}>
        <Box sx={{ opacity: truck.isActive === false ? 0.6 : 1, pointerEvents: truck.isActive === false ? 'none' : 'auto' }} aria-disabled={truck.isActive === false || undefined}>
          <FormControlLabel
            control={
              <Switch
                checked={values.swappable}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setValues((prev) => ({
                    ...prev,
                    swappable: isChecked,
                    swapBodyType: isChecked ? prev.swapBodyType : undefined  // Clear swapBodyType when swappable is unchecked
                  }));
                }}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {t('Swappable Body')}
                <InfoTooltip
                  title={t('Swappable Body')}
                  description={
                    <>
                      {t('Allows the trailer to use interchangeable cargo bodies (e.g. swap containers).')}
                      <br />
                      {t('Commonly used in hub-based or multi-leg transport operations.')}
                    </>
                  }
                  metaHint={t('Only enable if your trailer supports this')}
                  icon={<SwapHoriz />}
                />
              </Box>
            }
          />
        </Box>
        <Box sx={{ mt: 2, pt: 3, borderTop: '1px solid', borderColor: 'border.divider' }}>
          {truck.isActive === false ? (
            <Button variant="outlined" color="success" startIcon={<RestoreIcon />} onClick={handleRestoreTruck} disabled={loading}>
              {t('RESTORE TRUCK')}
            </Button>
          ) : (
            <DeleteButton onClick={() => setDeleteConfirmOpen(true)}>
              {t('DELETE TRUCK')}
            </DeleteButton>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderLocationParameter = (location: LocationParameter | undefined, label: string) => {
    if (!location) return null;

    const isDestination = label === t('Destination Location');

    const renderPostalCodeAreas = (areas: PostalCodeArea[]) => (
      <Box>
        {areas.map((area: PostalCodeArea, idx: number) => {
          const countryName = COUNTRY_NAMES[area.country as CountryCode] || area.country;
          const hasPostalCodes = area.postalCodes && area.postalCodes.length > 0;
          return (
            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
              {countryName}{hasPostalCodes ? `: ${area.postalCodes.join(', ')}` : ''}
            </Typography>
          );
        })}
      </Box>
    );

    const content = location.objectType === 'postalCodeAreasArray' ? (
      isDestination ? (
        <Box 
          sx={{ 
            maxHeight: '170px',
            overflowY: 'auto',
            paddingRight: '4px',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              }
            }
          }}
        >
          {renderPostalCodeAreas(location.postalCodeAreas)}
        </Box>
      ) : (
        renderPostalCodeAreas(location.postalCodeAreas)
      )
    ) : location.objectType === 'areaSearch' ? (
      <Box>
        <Typography variant="body2">
          {[
            location.area.address.city,
            location.area.address.postalCode,
            COUNTRY_NAMES[location.area.address.country as CountryCode] || location.area.address.country
          ].filter(Boolean).join(', ')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.table.header, mt: 0.5 }}>
          {t('Radius')}: {location.area.size_km}km
        </Typography>
      </Box>
    ) : null;

    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Box sx={{ mt: 1 }}>
          {content}
        </Box>
      </Box>
    );
  };

  const renderSearchHistory = () => (
    <Box sx={{
      ...styles.sectionContainer,
      display: 'flex',
      flexDirection: 'column',
      height: '100%'    
    }}>
      <Typography variant="h6" sx={{
        ...styles.sectionHeading,
        mb: { xs: 1.5, sm: 2 },
      }}>
        {t('Search History')}
      </Typography>
      {searchHistory.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
          <EmptyState
            title={t("Oops! There's nothing here...")}
            description={
              <>
                {t('There is nothing here to view right now.')}
                <br />
                {t('Search history will appear here once you enable Auto Search and the system starts searching for loads automatically.')}
              </>
            }
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
          {searchHistory.map((search, index) => (
            <Box
              key={`${search.sessionId}-${index}`}
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                border: '1px solid border.default',
                borderRadius: 2,
                backgroundColor: 'background.grey',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                mb: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Box>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    fontSize: (theme: any) => theme.typography.fontSizes.heading
                  }}>
                    {t('Search Session')} #{search.sessionId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.table.header }}>
                    {search.searchedAt ? new Date(search.searchedAt).toLocaleString() : t('Unknown date')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Chip
                    label={`${t('Executions')}: ${search.history?.length || 0}`}
                    size="small"
                    sx={{
                      backgroundColor: 'background.blue',
                      color: 'text.blue',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(auto-fit, minmax(250px, 1fr))'
                },
                gap: { xs: 1.5, sm: 2 }
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontWeight: 600,
                    fontSize: (theme: any) => theme.typography.fontSizes.small
                  }}>
                    {t('Marketplace')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.header }}>
                    {search.marketplace || t('N/A')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontWeight: 600,
                    fontSize: (theme: any) => theme.typography.fontSizes.small
                  }}>
                    {t('Mode')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.header }}>
                    {search.mode || t('N/A')}
                  </Typography>
                </Box>
                {search.shouldReturnRegCountry !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('Return to registration country')}
                    </Typography>
                    <Typography variant="body2">
                      {search.shouldReturnRegCountry ? t('Yes') : t('No')}
                    </Typography>
                  </Box>
                )}
                {search.destinationSource && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('Destination Source')}
                    </Typography>
                    <Typography variant="body2">{search.destinationSource}</Typography>
                  </Box>
                )}
                {search.history?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('Initial Marketplace Offers')}
                    </Typography>
                    <Typography variant="body2">{search.history[0].marketplaceOffersFound}</Typography>
                  </Box>
                )}
                {search.history?.length > 1 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('Marketplace Offers Found On Refresh')}
                    </Typography>
                    <Typography variant="body2">{search.history[search.history.length - 1].marketplaceOffersFound}</Typography>
                  </Box>
                )}
                {search.history?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('Potential Offers Found')}
                    </Typography>
                    <Typography variant="body2">{search.history[search.history.length - 1].potentialOffersFound}</Typography>
                  </Box>
                )}
              </Box>

              {search.searchParameters && (
                <>
                  <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      {t('Search Parameters')}
                    </Typography>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(auto-fit, minmax(250px, 1fr))'
                      },
                      gap: { xs: 1.5, sm: 2 }
                    }}>
                      {search.searchParameters.exclusiveLeftLowerBoundDateTime && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Date Range Start')}
                          </Typography>
                          <Typography variant="body2">
                            {new Date(search.searchParameters.exclusiveLeftLowerBoundDateTime).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {search.searchParameters.inclusiveRightUpperBoundDateTime && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Date Range End')}
                          </Typography>
                          <Typography variant="body2">
                            {new Date(search.searchParameters.inclusiveRightUpperBoundDateTime).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {search.searchParameters.loadingDate?.start && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Loading Date Start')}
                          </Typography>
                          <Typography variant="body2">
                            {new Date(search.searchParameters.loadingDate.start).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {search.searchParameters.loadingDate?.end && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Loading Date End')}
                          </Typography>
                          <Typography variant="body2">
                            {new Date(search.searchParameters.loadingDate.end).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {search.searchParameters.lengthRangeM !== undefined &&
                        tenant?.minLengthMForFullTruckLoad !== undefined &&
                        search.searchParameters.lengthRangeM.inclusiveMinimum >= tenant.minLengthMForFullTruckLoad && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Full Truck Load')}
                          </Typography>
                          <Typography variant="body2">
                            {t('Yes')} (≥{tenant.minLengthMForFullTruckLoad}m)
                          </Typography>
                        </Box>
                      )}
                      {renderLocationParameter(search.searchParameters.startLocation, t('Start Location'))}
                      {renderLocationParameter(search.searchParameters.destinationLocation, t('Destination Location'))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const handleFetchStats = async () => {
    if (!statsFrom || !statsTo) {
      toast.error(t('Please select both a from and to date'));
      return;
    }
    const fromMs = new Date(statsFrom).getTime();
    const toMs = new Date(statsTo).getTime();
    if (isNaN(fromMs) || isNaN(toMs)) {
      toast.error(t('Invalid date range'));
      return;
    }
    if (fromMs >= toMs) {
      toast.error(t('From date must be before to date'));
      return;
    }
    setStatsLoading(true);
    setStatsError(null);
    try {
      const result = await getVehicle(truck.id, fromMs, toMs);
      setStatsBookings((result as any).bookings ?? []);
    } catch {
      setStatsError(t('Failed to fetch booking stats'));
    } finally {
      setStatsLoading(false);
    }
  };

  const parseDatePickerValue = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
      return (value as { toDate: () => Date }).toDate();
    }
    if (Object.prototype.toString.call(value) === '[object Date]') {
      return value as Date;
    }
    if (typeof value === 'string') {
      return new Date(value);
    }
    return undefined;
  };

  const renderStats = () => {
    const totalBookings = statsBookings?.length ?? 0;
    const totalDistanceKms = statsBookings?.reduce((sum, b) => sum + (b.distanceKms || 0), 0) ?? 0;
    const totalRevenue = statsBookings?.reduce((sum, b) => sum + (b.price || 0), 0) ?? 0;

    return (
      <Box sx={styles.sectionContainer}>
        <Typography variant="h6" sx={styles.sectionHeading}>
          {t('Booking Stats')}
        </Typography>
        <Box sx={{ ...styles.gridContainer, alignItems: 'flex-end' }}>
          <DateTimeInput
            label={t('From')}
            type="date"
            fullWidth
            size="small"
            value={formatDateTimeLocal(statsFrom) || ''}
            onChange={(value) => setStatsFrom(parseDatePickerValue(value))}
          />
          <DateTimeInput
            label={t('To')}
            type="date"
            fullWidth
            size="small"
            value={formatDateTimeLocal(statsTo) || ''}
            onChange={(value) => setStatsTo(parseDatePickerValue(value))}
            minDate={statsFrom ? dayjs(statsFrom) : undefined}
          />
          <Button
            variant="contained"
            onClick={handleFetchStats}
            disabled={statsLoading}
            sx={{ height: 40 }}
          >
            {statsLoading ? <CircularProgress size={20} color="inherit" /> : t('Get Stats')}
          </Button>
        </Box>

        {statsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {statsError}
          </Alert>
        )}

        {statsBookings !== null && !statsError && (
          <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Bookings')}</Typography>
              <Typography variant="h6">{totalBookings}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Total Distance (km)')}</Typography>
              <Typography variant="h6">{totalDistanceKms.toFixed(0)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Total Revenue')}</Typography>
              <Typography variant="h6">{totalRevenue.toFixed(2)}</Typography>
            </Box>
          </Box>
        )}

        {statsBookings !== null && statsBookings.length > 0 && (
          <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {statsBookings.map((booking) => (
              <Box
                key={booking.id}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  {t('Booking')} #{booking.id} — {booking.createdAt ? formatDateTime(booking.createdAt) : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.distanceKms ?? 0} km · {booking.price ?? 0}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const isInactive = truck.isActive === false;
  const lockIfInactive = (content: React.ReactNode) => isInactive ? (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('This truck is deleted. Restore it on the Settings tab to edit its details.')}
      </Alert>
      <Box sx={{ opacity: 0.6, pointerEvents: 'none' }} aria-disabled="true">
        {content}
      </Box>
    </Box>
  ) : content;

  const tabs: TabConfig[] = [
    {
      label: t('Truck Bookings'),
      content: <BookingsTable vehicle={truck} autoSearchSession={autoSearchSession} autoNegoSession={autoNegoSession} onVehicleUpdate={refreshVehicles} tenantTemplate={tenant?.bookingFileHtmlTemplate} />
    },
    {
      label: t('Truck Details'),
      content: lockIfInactive(renderTruckDetails())
    },
    {
      label: t('Driver Details'),
      content: lockIfInactive(renderDriverDetails())
    },
    {
      label: t('Location'),
      content: lockIfInactive(renderLocationDetails())
    },
    {
      label: t('Settings'),
      content: renderSettings()
    },
    {
      label: t('Booking Stats'),
      content: renderStats()
    },
    {
      label: t('Search History'),
      content: lockIfInactive(renderSearchHistory())
    }
  ];

  return (
    <>
      <StandardDialog
        open={open}
        onClose={onClose}
        title={truck.truckPlate}
        subtitle={truck.trailerPlate ? `${truck.trailerPlate} • ${t('Truck Settings')}` : t('Truck Settings')}
        tabs={tabs}
        maxWidth="xl"
        width={{
          xs: '95%',
          sm: '92%',
          md: '88%',
          lg: '85%',
          xl: '80%'
        }}
        currentValues={values}
        initialValues={initialValues}
        normalizeValues={normalizeValues}
        height={{ xs: '95vh', sm: '92vh', md: '90vh', lg: '88vh', xl: '88vh' }}
        error={error}
        headerActions={
          <Box
            aria-disabled={isInactive || undefined}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', opacity: isInactive ? 0.5 : 1 }}
          >
            <AutoSearchBadge
              session={autoSearchSession}
              loading={loading}
              onClick={isInactive ? undefined : handleAutoSearchClick}
              showLabel={true}
            />
            <AutoNegoBadge
              session={autoNegoSession}
              loading={loading}
              onClick={isInactive ? undefined : handleAutoNegoClick}
              showLabel={true}
            />
          </Box>
        }
        primaryAction={{
          label: t('Save Changes'),
          onClick: handleSave,
          loading: loading,
          disabled: loading || isInactive,
          usePartialUpdate: true // Enable partial updates - only send changed fields
        }}
        secondaryAction={{
          label: t('Cancel'),
          onClick: onClose
        }}
      />

      {/* Auto Search Dialog */}
      <AutoSearchDialog
        open={autoSearchModalOpen}
        vehicleId={truck?.id || 0}
        searchSession={autoSearchSession}
        onClose={() => setAutoSearchModalOpen(false)}
        onSuccess={handleAutoSessionSuccess}
      />
      {/* Auto Nego Dialog */}
      <AutoNegoDialog
        open={autoNegoModalOpen}
        vehicleId={truck?.id || 0}
        session={autoNegoSession}
        autoSearchSession={autoSearchSession}
        onClose={() => setAutoNegoModalOpen(false)}
        onSuccess={handleAutoSessionSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: 'text.darkGrey',
              mb: 1
            }}>
              {t('Delete Truck')} {truck.truckPlate}
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.grey',
              mb: 3
            }}>
              {t('Are you sure you want to delete this truck? You can find and restore deleted trucks later on the "Inactive" tab.')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: 2,
                borderColor: 'border.medium',
                color: 'text.darkGrey',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'border.dark',
                  backgroundColor: 'background.lightGrey'
                }
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleDeleteTruck}
              variant="contained"
              fullWidth
              disabled={deleteLoading}
              sx={{
                borderRadius: 2,
                backgroundColor: 'background.red',
                fontWeight: 500,
                textTransform: 'none',
                '&:disabled': {
                  backgroundColor: 'background.darkGrey'
                }
              }}
            >
              {deleteLoading ? <CircularProgress size={20} color="inherit" /> : t('Delete')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
